import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiSearch, FiBell, FiChevronDown, FiZap, FiUser, FiSettings, 
  FiAward, FiHeart, FiLogOut, FiArrowRight, FiStar, FiCheckCircle, 
  FiVideo, FiActivity, FiArrowUpRight, FiSmartphone, FiSend, FiX, FiCheck, FiGrid, FiClock, FiLock
} from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa";
import { useCheckIn } from "../../context/CheckInContext";
import { notificationAPI, practiceVideoAPI, liveSessionAPI, userAPI } from "../../services/api";
import { isFeaturePermitted } from "../../utils/featureAccess";

/* ─── Styles ─────────────────────────────────────────────────────────── */
const glass = {
  background: "rgba(255,255,255,0.78)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)",
};

const dropdownGlass = {
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(244,217,181,0.5)",
  backdropFilter: "blur(40px)",
  WebkitBackdropFilter: "blur(40px)",
  boxShadow: "0 30px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(242,154,12,0.08)",
};

/* ─── Helper for Web Push Key Conversion ─────────────────────────────── */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/* ─── Auth helpers ───────────────────────────────────────────────────── */
const getAuthState = () => {
  try {
    const token = localStorage.getItem("divyayoga_token");
    const role  = localStorage.getItem("divyayoga_role");
    const user  = JSON.parse(localStorage.getItem("divyayoga_user") || "null");
    if (token && user) return { loggedIn: true, role, user };
  } catch {}
  return { loggedIn: false, role: null, user: null };
};

const clearAuth = () => {
  localStorage.removeItem("divyayoga_token");
  localStorage.removeItem("divyayoga_user");
  localStorage.removeItem("divyayoga_role");
};

const getInitials = (user) => {
  if (!user) return "U";
  const name = user.name || user.fullName || user.userName || user.email || "";
  return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "U";
};

/* ─── Target Route Resolver for Notifications ──────────────────────── */
const getNotificationTarget = (n) => {
  if (!n) return "/user/notification";
  const title = (n.title || "").toLowerCase();
  const msg = (n.message || "").toLowerCase();

  if (title.includes("goal") || msg.includes("goal") || msg.includes("badge")) return "/user/goals";
  if (title.includes("class") || title.includes("session") || msg.includes("session")) return "/user/livesession";
  if (title.includes("video") || msg.includes("practice")) return "/user/traininglibrary";
  if (title.includes("diet") || msg.includes("meal")) return "/user/dietplan";

  return "/user/notification";
};

/* ─── Dropdown menu items ────────────────────────────────────────────── */
const menuItems = [
  { icon: FiUser,     label: "My Profile",      sub: "View & edit profile",     path: "/user/profile",      accent: "#F29A0C" },
  { icon: FiAward,    label: "Achievements",    sub: "Badges & milestones",     path: "/user/achievements", accent: "#F29A0C" },
  { icon: FiHeart,    label: "Wellness Goals",  sub: "Track your progress",     path: "/user/goals",        accent: "#F29A0C" },
  { icon: FiSettings, label: "Settings",        sub: "Account preferences",     path: "/user/settings",     accent: "#F29A0C" },
];

/* ─── User Dropdown ──────────────────────────────────────────────────── */
export function UserDropdown({ auth, onLogout, stats, profile, positionClass = "absolute right-0 top-[calc(100%+12px)] w-[320px]" }) {
  const userName   = profile?.fullName || auth.user?.name || auth.user?.fullName || auth.user?.userName || "Account";
  const userEmail  = profile?.email || auth.user?.email || "user@user.com";

  const rawTier = profile?.tier || profile?.activeSubscription?.tier || profile?.activeSubscription?.packageName || auth.user?.tier || auth.user?.subscriptionTier || "Free Trial";
  const cleanTier = rawTier.trim();
  const formattedTier = cleanTier.toLowerCase().endsWith("member") ? cleanTier : `${cleanTier} Member`;

  const roleLabel  = auth.role === "admin" ? "Admin" : formattedTier;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0,  scale: 1    }}
      exit={{  opacity: 0, y: 8,   scale: 0.97  }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className={`${positionClass} rounded-[28px] overflow-hidden z-[9999]`}
      style={dropdownGlass}
    >
      {/* Gold top accent */}
      <div className="h-[3px]" style={{ background: "linear-gradient(90deg,#F29A0C,#FFD166,#E97818)" }} />

      {/* User info header */}
      <div className="px-5 pt-5 pb-4 border-b border-[#F4D9B5]/40">
        <div className="flex items-center gap-3.5">
          <div className="relative">
            <div className="w-14 h-14 rounded-[18px] flex items-center justify-center text-white text-[18px] font-black flex-shrink-0 overflow-hidden"
              style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", fontFamily: "'Poppins',sans-serif",
                boxShadow: "0 8px 20px rgba(242,154,12,0.40)" }}>
              {(() => {
                const avatarUrl = profile?.profileImageUrl || profile?.avatarUrl || auth.user?.profileImageUrl || auth.user?.avatarUrl;
                if (avatarUrl) {
                  return <img src={avatarUrl} alt="User Avatar" className="w-full h-full object-cover rounded-[18px]" />;
                }
                return getInitials(auth.user);
              })()}
            </div>
            {/* Online dot */}
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#6B8A5A] border-2 border-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-black text-[#1F1F1F] truncate" style={{ fontFamily:"'Poppins',sans-serif" }}>
              {userName}
            </p>
            <p className="text-[12px] text-[#AAA] truncate mt-0.5">{userEmail}</p>
            <div className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full"
              style={{ background: "rgba(242,154,12,0.10)", border: "1px solid rgba(242,154,12,0.20)" }}>
              <FiStar size={9} style={{ color:"#F29A0C" }}/>
              <span className="text-[10px] font-bold text-[#C8660A] uppercase tracking-wide">{roleLabel}</span>
            </div>
          </div>
        </div>

        {/* Quick stats row */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { val: stats?.streakDays || 0, label: "Day Streak", accent: "#F29A0C" },
            { val: stats?.totalPractices || 0, label: "Sessions",  accent: "#6B8A5A" },
            { val: stats?.wellnessScore ?? 0,  label: "Score",     accent: "#F29A0C" },
          ].map((s) => (
            <div key={s.label} className="rounded-[14px] p-2.5 text-center"
              style={{ background: `${s.accent}0D`, border: `1px solid ${s.accent}1A` }}>
              <p className="text-[16px] font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>{s.val}</p>
              <p className="text-[9px] font-bold text-[#AAA] uppercase tracking-wide mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Menu items */}
      <div className="px-3 py-3">
        {(() => {
          const isAchAllowed = isFeaturePermitted(profile, "achievements");
          const isGoalsAllowed = isFeaturePermitted(profile, "goals_tracker");

          const dynamicMenuItems = [
            { icon: FiUser, label: "My Profile", sub: "View & edit profile", path: "/user/profile", accent: "#F29A0C", isLocked: false },
            { icon: isAchAllowed ? FiAward : FiLock, label: "Achievements", sub: isAchAllowed ? "Badges & milestones" : "Locked · Upgrade Tier", path: "/user/achievements", accent: isAchAllowed ? "#F29A0C" : "#888888", isLocked: !isAchAllowed },
            { icon: isGoalsAllowed ? FiHeart : FiLock, label: "Wellness Goals", sub: isGoalsAllowed ? "Track your progress" : "Locked · Upgrade Tier", path: "/user/goals", accent: isGoalsAllowed ? "#F29A0C" : "#888888", isLocked: !isGoalsAllowed },
            { icon: FiSettings, label: "Settings", sub: "Account preferences", path: "/user/settings", accent: "#F29A0C", isLocked: false },
          ];

          return dynamicMenuItems.map((item) => (
            <motion.div key={item.label} whileHover={{ x: 4, backgroundColor: `${item.accent}08` }}
              transition={{ duration: 0.15 }}
              className="rounded-[16px] overflow-hidden">
              <Link to={item.path}
                className="flex items-center gap-3 px-3 py-2.5 group">
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0 transition-all duration-200"
                  style={{ background: `${item.accent}12` }}
                >
                  <item.icon size={15} style={{ color: item.accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#1F1F1F] flex items-center gap-1.5">
                    <span>{item.label}</span>
                    {item.isLocked && (
                      <span className="text-[9px] font-extrabold px-1.5 py-0.2 bg-zinc-200 text-zinc-600 rounded-full">Locked</span>
                    )}
                  </p>
                  <p className="text-[11px] text-[#BBB] truncate">{item.sub}</p>
                </div>
                <FiArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ color: item.accent }} />
              </Link>
            </motion.div>
          ));
        })()}
      </div>

      {/* Logout */}
      <div className="px-3 pb-4">
        <div className="h-[1px] bg-[#F4D9B5]/40 mb-3" />
        <motion.button
          whileHover={{ backgroundColor: "rgba(239,68,68,0.06)", x: 4 }}
          transition={{ duration: 0.15 }}
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[16px] transition-colors duration-200"
        >
          <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(239,68,68,0.08)" }}>
            <FiLogOut size={15} style={{ color: "#EF4444" }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-[13px] font-semibold text-[#EF4444]">Sign Out</p>
            <p className="text-[11px] text-[#BBB]">See you soon!</p>
          </div>
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ─── Main Topbar ────────────────────────────────────────────────────── */
export default function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dropRef  = useRef(null);
  const notifDropRef = useRef(null);

  const { checkedIn, checkIn, checkOut, loading: checkInLoading, stats, unreadNotifCount } = useCheckIn();

  const [auth,     setAuth]     = useState(getAuthState);
  const [profile,  setProfile]  = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [selectedNotif, setSelectedNotif] = useState(null);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushStatusMsg, setPushStatusMsg] = useState("");

  const fetchUserProfile = async () => {
    const currentAuth = getAuthState();
    setAuth(currentAuth);
    if (currentAuth.loggedIn && currentAuth.role === "user") {
      userAPI.getProfile().then(res => {
        if (res.data?.success) setProfile(res.data.data);
      }).catch(e => console.warn(e));
    }
  };

  useEffect(() => {
    fetchUserProfile();

    const handleUpdate = () => fetchUserProfile();
    window.addEventListener("profile-updated", handleUpdate);
    window.addEventListener("subscription-updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener("profile-updated", handleUpdate);
      window.removeEventListener("subscription-updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, [location.pathname]);

  const [query,    setQuery]    = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [videos, setVideos] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => { setAuth(getAuthState()); }, [location.pathname]);

  // Check browser Web Push permission on load
  useEffect(() => {
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  const loadSearchSources = async () => {
    try {
      const [sessRes, vidRes] = await Promise.allSettled([
        liveSessionAPI.getSessions(),
        practiceVideoAPI.getVideos()
      ]);
      if (sessRes.status === "fulfilled" && sessRes.value.data?.success) {
        setSessions(sessRes.value.data.data || []);
      }
      if (vidRes.status === "fulfilled" && vidRes.value.data?.success) {
        setVideos(vidRes.value.data.data || []);
      }
    } catch (e) {
      console.warn("Failed to load user search sources:", e);
    }
  };

  useEffect(() => {
    if (auth.loggedIn && auth.role === "user") {
      loadSearchSources();
    }
  }, [auth.loggedIn, auth.role]);

  const fetchRecentNotifications = async () => {
    try {
      const res = await notificationAPI.getNotifications();
      if (res.data?.success && res.data.data) {
        setNotifications(res.data.data.slice(0, 5));
      }
    } catch (e) {
      console.warn("Failed to fetch topbar notifications:", e);
    }
  };

  useEffect(() => {
    if (auth.loggedIn && auth.role === "user") {
      fetchRecentNotifications();
    }
  }, [auth.loggedIn, auth.role, unreadNotifCount]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
      if (notifDropRef.current && !notifDropRef.current.contains(e.target)) setNotifOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setIsFocused(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    clearAuth();
    setAuth({ loggedIn: false, role: null, user: null });
    setDropOpen(false);
    navigate("/");
  };

  /* Interactive notification click handler */
  const handleNotificationClick = async (n) => {
    if (!n.isRead) {
      notificationAPI.markAsRead(n.id).catch(() => null);
      setNotifications(prev => prev.map(item => item.id === n.id ? { ...item, isRead: true } : item));
    }
    setSelectedNotif(n);
  };

  /* Enable Web Push Notification handler */
  const handleEnablePush = async () => {
    if (!('Notification' in window)) {
      setPushStatusMsg("Push not supported on this browser");
      setTimeout(() => setPushStatusMsg(""), 3500);
      return;
    }
    try {
      setPushLoading(true);
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        
        // Show instant confirmation notification
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.ready.catch(() => null);
          if (reg && reg.showNotification) {
            reg.showNotification("🧘 Push Notifications Enabled", {
              body: "You will now receive real-time class & goal updates!",
              icon: "/favicon.ico"
            }).catch(() => null);
          } else {
            new Notification("🧘 Push Notifications Enabled", {
              body: "You will now receive real-time class & goal updates!",
              icon: "/favicon.ico"
            });
          }
        }

        // Subscribe with backend VAPID public key
        const vapidRes = await notificationAPI.getVapidPublicKey().catch(() => null);
        const vapidKey = vapidRes?.data?.data || vapidRes?.data?.publicVapidKey || "BF2Vd_sample_key";

        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.ready.catch(() => null);
          if (reg) {
            const sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidKey)
            }).catch(() => null);

            if (sub) {
              const subJson = sub.toJSON();
              await notificationAPI.subscribePush({
                endpoint: subJson.endpoint,
                p256dh: subJson.keys?.p256dh || '',
                auth: subJson.keys?.auth || ''
              }).catch(() => null);
            }
          }
        }
        setPushStatusMsg("✓ Push notifications enabled!");
      } else if (permission === 'denied') {
        setPushStatusMsg("⚠️ Notifications blocked in browser settings");
      }
      setTimeout(() => setPushStatusMsg(""), 4000);
    } catch (err) {
      console.warn("Push notification permission error:", err);
    } finally {
      setPushLoading(false);
    }
  };

  /* Send Test Push Notification */
  const handleTestPush = async () => {
    try {
      setPushLoading(true);
      await notificationAPI.sendTestPush().catch(() => null);

      if (!('Notification' in window)) {
        setPushStatusMsg("Push not supported on this browser");
        setTimeout(() => setPushStatusMsg(""), 3500);
        return;
      }

      let currentPermission = Notification.permission;
      if (currentPermission === 'default') {
        currentPermission = await Notification.requestPermission();
        if (currentPermission === 'granted') setPushEnabled(true);
      }

      if (currentPermission === 'granted') {
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.ready.catch(() => null);
          if (reg && reg.showNotification) {
            await reg.showNotification("🧘 Divyyog Live Alert", {
              body: "Real-time push notifications are active on your device!",
              icon: "/favicon.ico",
              tag: "divyayoga-test"
            }).catch(() => null);
          } else {
            new Notification("🧘 Divyyog Live Alert", {
              body: "Real-time push notifications are active on your device!",
              icon: "/favicon.ico"
            });
          }
        } else {
          new Notification("🧘 Divyyog Live Alert", {
            body: "Real-time push notifications are active on your device!",
            icon: "/favicon.ico"
          });
        }
        setPushStatusMsg("✓ Test push sent to device!");
      } else {
        setPushStatusMsg("⚠️ Please click 🔒 icon in browser URL bar to allow notifications!");
      }

      setTimeout(() => setPushStatusMsg(""), 4000);
    } catch (e) {
      console.warn("Test push error:", e);
      setPushStatusMsg("Failed to dispatch push");
      setTimeout(() => setPushStatusMsg(""), 3500);
    } finally {
      setPushLoading(false);
    }
  };

  const USER_PAGES = [
    { name: "My Dashboard", path: "/user/dashboard", desc: "Main control panel & overview", icon: FiGrid },
    { name: "Training Library", path: "/user/traininglibrary", desc: "Browse practice yoga videos", icon: FiVideo },
    { name: "Live Sessions Roster", path: "/user/livesession", desc: "Book or join live online interactive classes", icon: FiActivity },
    { name: "Notifications Panel", path: "/user/notification", desc: "Updates & announcements history", icon: FiBell },
    { name: "My Profile Details", path: "/user/profile", desc: "Update billing address and phone", icon: FiSettings }
  ];

  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();

    const matchedPages = USER_PAGES.filter(p => 
      p.name.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q)
    );

    const matchedSessions = sessions.filter(s => 
      s.title?.toLowerCase().includes(q) || s.instructorName?.toLowerCase().includes(q)
    ).map(s => ({
      name: s.title,
      desc: `Live Class | Instructor: ${s.instructorName} | Duration: ${s.durationInMinutes}m`,
      path: `/user/live-session?search=${encodeURIComponent(s.title)}`,
      icon: FiActivity
    }));

    const matchedVideos = videos.filter(v => 
      v.title?.toLowerCase().includes(q) || v.description?.toLowerCase().includes(q)
    ).map(v => {
      let vDesc = "";
      if (v.description && v.description.startsWith("{")) {
        try {
          const parsed = JSON.parse(v.description);
          vDesc = parsed.desc || parsed.Description || "";
        } catch {}
      } else {
        vDesc = v.description;
      }
      return {
        name: v.title,
        desc: `Practice Library | ${vDesc || "Yoga flow video"}`,
        path: `/user/traininglibrary?search=${encodeURIComponent(v.title)}`,
        icon: FiVideo
      };
    });

    const results = [
      { category: "System Pages & Navigation", list: matchedPages },
      { category: "Live Sessions & Classes", list: matchedSessions },
      { category: "Practice Videos & Tutorials", list: matchedVideos }
    ].filter(g => g.list.length > 0);

    return results;
  }, [query, sessions, videos]);

  const flattenedResults = useMemo(() => {
    if (!searchResults) return [];
    return searchResults.reduce((acc, group) => [...acc, ...group.list], []);
  }, [searchResults]);

  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  const handleKeyDown = (e) => {
    if (!flattenedResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % flattenedResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + flattenedResults.length) % flattenedResults.length);
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < flattenedResults.length) {
        e.preventDefault();
        const target = flattenedResults[activeIndex];
        setIsFocused(false);
        setQuery("");
        navigate(target.path);
      }
    } else if (e.key === "Escape") {
      setIsFocused(false);
    }
  };

  const userName = auth.user?.name || auth.user?.fullName || auth.user?.userName || "Account";

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 mb-5 sm:mb-6 px-3.5 sm:px-5 py-2.5 sm:py-3.5 rounded-[22px] sm:rounded-[26px] relative z-[40]"
      style={glass}
    >
      {/* Search */}
      <div className="relative flex-1 w-full max-w-full sm:max-w-md" ref={searchRef}>
        <div className="flex items-center gap-3 w-full">
          <div className="w-9 h-9 rounded-[13px] flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(242,154,12,0.1)" }}>
            <FiSearch style={{ color: "#F29A0C", fontSize: 16 }} />
          </div>
          <input 
            placeholder="Search sessions, library, pages…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none text-sm text-[#1F1F1F] placeholder-[#CCC]"
            style={{ fontFamily: "'Inter',sans-serif" }} 
          />
        </div>

        {/* Dropdown Suggestions */}
        <AnimatePresence>
          {isFocused && searchResults && searchResults.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 top-[calc(100%+14px)] w-[440px] max-h-[380px] overflow-y-auto rounded-[24px] p-4 z-[9999]"
              style={dropdownGlass}
            >
              <div className="flex flex-col gap-4">
                {searchResults.map((group) => (
                  <div key={group.category}>
                    <p className="text-[10px] font-black text-[#C8660A] uppercase tracking-wider mb-2 px-2.5">
                      {group.category}
                    </p>
                    <div className="flex flex-col gap-1">
                      {group.list.map((item) => {
                        const globalIdx = flattenedResults.indexOf(item);
                        const isSelected = globalIdx === activeIndex;
                        return (
                          <motion.div
                            key={item.name + item.path}
                            whileHover={{ x: 4 }}
                            onClick={() => {
                              setIsFocused(false);
                              setQuery("");
                              navigate(item.path);
                            }}
                            className="rounded-[14px] overflow-hidden cursor-pointer"
                            style={{
                              background: isSelected ? "rgba(242,154,12,0.06)" : "transparent"
                            }}
                          >
                            <div className="flex items-center gap-3 px-3 py-2.5 group">
                              <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 transition-all duration-200"
                                style={{ background: isSelected ? "rgba(242,154,12,0.12)" : "rgba(242,154,12,0.06)" }}
                              >
                                <item.icon size={14} style={{ color: "#F29A0C" }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-[#1F1F1F] group-hover:text-[#F29A0C] transition-colors duration-150">
                                  {item.name}
                                </p>
                                <p className="text-[10px] text-[#AAA] truncate mt-0.5">
                                  {item.desc}
                                </p>
                              </div>
                              <FiArrowUpRight size={13} className="text-[#AAA] opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Right controls */}
      <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">

        {/* Check-In / Check-Out Button */}
        {auth.loggedIn && auth.role === "user" && (
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.95 }}
            disabled={checkInLoading}
            onClick={checkedIn ? checkOut : checkIn}
            className="flex-1 sm:flex-initial justify-center flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 cursor-pointer border select-none whitespace-nowrap"
            style={{
              background: checkedIn ? "rgba(226, 75, 74, 0.08)" : "rgba(107, 138, 90, 0.08)",
              borderColor: checkedIn ? "rgba(226, 75, 74, 0.25)" : "rgba(107, 138, 90, 0.25)",
              color: checkedIn ? "#E24B4A" : "#6B8A5A",
              fontFamily: "'Inter', sans-serif"
            }}
          >
            {checkedIn ? (
              <>
                <FiLogOut size={13} />
                Check Out
              </>
            ) : (
              <>
                <FiCheckCircle size={13} />
                Check In
              </>
            )}
          </motion.button>
        )}

        {/* Streak badge */}
        {auth.loggedIn && auth.role === "user" && (
          <motion.div
            animate={{ boxShadow: ["0 0 16px rgba(242,154,12,0.3)","0 0 32px rgba(242,154,12,0.6)","0 0 16px rgba(242,154,12,0.3)"] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            className="flex-1 sm:flex-initial justify-center flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-full text-white text-xs font-bold whitespace-nowrap shrink-0"
            style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }}
          >
            <FiZap size={14} /> {stats?.streakDays || 0} Day Streak
          </motion.div>
        )}

        {/* Bell + Notifications Popover (desktop / tablet only, mobile has it in sticky header) */}
        {auth.loggedIn && auth.role === "user" ? (
          <div className="hidden sm:block relative z-[9999]" ref={notifDropRef}>
            <div 
              onClick={() => setNotifOpen(prev => !prev)}
              className="relative w-9 h-9 rounded-[13px] flex items-center justify-center cursor-pointer select-none"
              style={{ background: "rgba(242,154,12,0.08)" }}
            >
              <FiBell style={{ color: "#F29A0C", fontSize: 17 }} />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-[#E97818] text-white text-[9px] font-extrabold flex items-center justify-center px-1 border border-[#FFF8EE]">
                  {unreadNotifCount}
                </span>
              )}
            </div>

            <AnimatePresence>
              {notifOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.97 }}
                  className="absolute right-0 top-[calc(100%+12px)] w-[380px] rounded-[28px] p-5 z-[9999] text-left"
                  style={dropdownGlass}
                >
                  {/* Top Bar Header */}
                  <div className="flex items-center justify-between mb-3 px-1">
                    <p className="text-[14px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>Recent Updates</p>
                    <span className="text-[11px] font-extrabold text-amber-800 bg-amber-500/15 px-3 py-1 rounded-full border border-amber-500/20">
                      {unreadNotifCount} New
                    </span>
                  </div>

                  {/* Web Push Subscription Card */}
                  <div className="mb-4 p-3.5 rounded-[20px] bg-amber-500/10 border border-amber-500/20 text-left">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <FiSmartphone className="text-amber-600" size={15} />
                        <span className="text-[12px] font-extrabold text-amber-950">Web Push Alerts</span>
                      </div>
                      <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${pushEnabled ? 'bg-emerald-600 text-white' : 'bg-amber-600/20 text-amber-900'}`}>
                        {pushEnabled ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-600 mb-2.5 leading-relaxed">
                      {pushEnabled ? 'You are subscribed to real-time browser push notifications.' : 'Enable instant browser push notifications for class reminders & achievements.'}
                    </p>
                    {pushStatusMsg ? (
                      <span className="text-[11px] font-extrabold text-emerald-800 bg-emerald-100/90 py-2 px-3 rounded-full text-center block w-full border border-emerald-300">
                        {pushStatusMsg}
                      </span>
                    ) : pushEnabled ? (
                      <button 
                        onClick={handleTestPush}
                        disabled={pushLoading}
                        className="w-full py-2 rounded-full text-[11px] font-extrabold bg-emerald-600 text-white flex items-center justify-center gap-1.5 shadow-sm hover:bg-emerald-700 transition-colors">
                        <FiSend size={12} /> {pushLoading ? 'Sending...' : 'Test Push Notification'}
                      </button>
                    ) : (
                      <button 
                        onClick={handleEnablePush}
                        disabled={pushLoading}
                        className="w-full py-2 rounded-full text-[11px] font-extrabold bg-amber-500 text-white flex items-center justify-center gap-1.5 shadow-sm hover:bg-amber-600 transition-colors">
                        <FiBell size={12} /> {pushLoading ? 'Subscribing...' : 'Enable Push Notifications'}
                      </button>
                    )}
                  </div>

                  {/* Notification Items List */}
                  <div className="flex flex-col gap-3 max-h-[340px] overflow-y-auto pr-1">
                    {notifications.length === 0 ? (
                      <p className="text-[12px] text-zinc-400 py-8 text-center font-medium">No new notifications</p>
                    ) : (
                      notifications.map(n => {
                        const isUnread = !n.isRead;
                        const dateObj = n.createdAt ? new Date(n.createdAt) : new Date();
                        const formattedDateStr = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                        const formattedTimeStr = dateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

                        return (
                          <motion.div 
                            key={n.id} 
                            whileHover={{ scale: 1.01, x: 2 }}
                            onClick={() => handleNotificationClick(n)}
                            className={`p-4 rounded-[20px] cursor-pointer transition-all duration-200 text-left relative border-l-4 ${
                              isUnread 
                                ? 'bg-amber-50/90 border-l-[#F29A0C] border-t border-r border-b border-amber-200/60 shadow-sm' 
                                : 'bg-white/80 border-l-zinc-300 border-t border-r border-b border-zinc-200/60'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <p className={`text-[13px] leading-tight ${isUnread ? 'font-black text-amber-950' : 'font-bold text-[#1F1F1F]'}`}>
                                {n.title}
                              </p>
                              {isUnread ? (
                                <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-[#F29A0C] text-white flex-shrink-0 flex items-center gap-1 shadow-sm">
                                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> NEW
                                </span>
                              ) : (
                                <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500 flex-shrink-0 flex items-center gap-1">
                                  <FiCheck size={10} /> Read
                                </span>
                              )}
                            </div>

                            <p className={`text-[11.5px] line-clamp-2 leading-relaxed mb-3 ${isUnread ? 'text-amber-900 font-medium' : 'text-zinc-500'}`}>
                              {n.message}
                            </p>

                            <div className="flex items-center justify-between pt-2 border-t border-black/5">
                              <span className="text-[10px] font-bold text-zinc-400 flex items-center gap-1">
                                <FiClock size={11} className="text-zinc-400" /> {formattedDateStr} at {formattedTimeStr}
                              </span>
                              <span className="text-[10px] font-extrabold text-[#C8660A] flex items-center gap-1">
                                Tap to View <FiArrowUpRight size={11} />
                              </span>
                            </div>
                          </motion.div>
                        );
                      })
                    )}
                  </div>
                  <div className="h-[1px] bg-[#F4D9B5]/40 my-3" />
                  <Link 
                    to="/user/notification" 
                    onClick={() => setNotifOpen(false)}
                    className="text-[12px] font-extrabold text-[#C8660A] hover:underline flex items-center justify-center gap-1.5 py-1"
                  >
                    View All Notifications <FiArrowRight size={13} />
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <div className="relative w-9 h-9 rounded-[13px] flex items-center justify-center cursor-pointer"
            style={{ background: "rgba(242,154,12,0.08)" }}>
            <FiBell style={{ color: "#F29A0C", fontSize: 17 }} />
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#E97818] border-2 border-[#FFF8EE]" />
          </div>
        )}

        {/* Auth (desktop only, mobile has avatar in sticky header) */}
        {auth.loggedIn ? (
          /* Avatar button + dropdown */
          <div className="hidden sm:block relative z-[9999]" ref={dropRef}>
            <motion.div
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => setDropOpen(prev => !prev)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-full cursor-pointer select-none transition-all duration-200 z-[9999]"
              style={{
                background: dropOpen ? "rgba(242,154,12,0.12)" : "rgba(255,244,232,0.9)",
                border: dropOpen ? "1px solid rgba(242,154,12,0.25)" : "1px solid transparent",
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-black overflow-hidden"
                style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)", fontFamily: "'Poppins',sans-serif" }}>
                {(() => {
                  const avatarUrl = profile?.profileImageUrl || profile?.avatarUrl || auth.user?.profileImageUrl || auth.user?.avatarUrl;
                  if (avatarUrl) {
                    return <img src={avatarUrl} alt="User Avatar" className="w-full h-full object-cover rounded-full" />;
                  }
                  return getInitials(auth.user);
                })()}
              </div>
              {(() => {
                const rawTier = profile?.tier || profile?.activeSubscription?.tier || profile?.activeSubscription?.packageName || auth.user?.tier || auth.user?.subscriptionTier || "Free Trial";
                const cleanTier = rawTier.trim();
                const formattedTier = cleanTier.toLowerCase().endsWith("member") ? cleanTier : `${cleanTier} Member`;
                const headerSubLabel = auth.role === "admin" ? "Admin" : formattedTier;

                return (
                  <div className="hidden lg:block">
                    <p className="text-xs font-bold text-[#1F1F1F]">{profile?.fullName || userName}</p>
                    <p className="text-[10px] text-[#AAA]">{headerSubLabel}</p>
                  </div>
                );
              })()}
              <motion.div animate={{ rotate: dropOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                <FiChevronDown style={{ color: "#BBB", fontSize: 14 }} />
              </motion.div>
            </motion.div>

            {/* Dropdown */}
            <AnimatePresence>
              {dropOpen && <UserDropdown auth={auth} onLogout={handleLogout} stats={stats} profile={profile} />}
            </AnimatePresence>
          </div>
        ) : (
          <>
            <Link to="/login">
              <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                className={`rounded-full px-6 py-3 text-[14px] font-medium border transition-all duration-300 ${
                  scrolled
                    ? "border-[#F4D9B5] bg-white text-[#111111] hover:border-[#F29A0C] hover:text-[#F29A0C]"
                    : "border-white/15 bg-white/10 text-white backdrop-blur-2xl hover:bg-white/20"
                }`}>
                Login
              </motion.button>
            </Link>
            <Link to="/register">
              <motion.button whileHover={{ y: -3, scale: 1.03 }} whileTap={{ scale: 0.97 }}
                className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] px-7 py-3 text-white text-[14px] font-semibold shadow-[0_20px_60px_rgba(242,154,12,0.25)]">
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.30),transparent)] skew-x-12" />
                <span className="relative z-20 flex items-center gap-3">Register <FaArrowRight /></span>
              </motion.button>
            </Link>
          </>
        )}
      </div>
    </motion.div>

      {/* NOTIFICATION DETAIL INTERACTIVE MODAL */}
      <AnimatePresence>
        {selectedNotif && (
          <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4" style={{ background: "rgba(0, 0, 0, 0.55)" }} onClick={() => setSelectedNotif(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 0 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-[28px] p-6 bg-white shadow-2xl border border-amber-200/60 relative overflow-hidden text-left my-auto z-[1000000]"
            >
              <button 
                onClick={() => setSelectedNotif(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 hover:bg-zinc-200 transition-colors">
                <FiX size={16} />
              </button>

              <div className="w-12 h-12 rounded-[16px] bg-amber-500/10 text-amber-600 flex items-center justify-center mb-4">
                <FiBell size={24} />
              </div>

              <h3 className="font-black text-[18px] text-[#1F1F1F] mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {selectedNotif.title}
              </h3>
              <p className="text-[11px] font-semibold text-amber-700 flex items-center gap-1 mb-4">
                <FiClock size={11} /> {selectedNotif.createdAt ? new Date(selectedNotif.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Recently"}
              </p>

              <div className="p-4 rounded-[18px] bg-amber-50/70 border border-amber-200/50 mb-6">
                <p className="text-[13px] text-zinc-700 leading-relaxed font-medium">
                  {selectedNotif.message}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const route = getNotificationTarget(selectedNotif);
                    setSelectedNotif(null);
                    setNotifOpen(false);
                    navigate(route);
                  }}
                  className="flex-1 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-extrabold text-[12px] flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 cursor-pointer">
                  Open Feature <FiArrowUpRight size={14} />
                </button>
                <button
                  onClick={() => setSelectedNotif(null)}
                  className="px-5 py-3 rounded-full bg-zinc-100 text-zinc-700 font-extrabold text-[12px] cursor-pointer">
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}